import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Button,
  makeStyles,
} from "@material-ui/core";
import { TextField } from "@ofqual/portal-components/components/TextField";
import { Select } from "@ofqual/portal-components/components/Select";
import React, { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  foiEventTypeService
} from "../../../services";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import {
  FoiEventComplete
} from "../../../domain/FoiEvent";
import { DateTimePicker } from "@ofqual/portal-components/components/DateTimePicker";
import { Error } from "../../common/Error";
import { useDispatch } from "react-redux";
import { completeFoiEvent } from "../../../redux/actions";
import { FoiItem } from "../../../domain/FoiItem";
import { FileInput } from "@ofqual/portal-components/components/FileInput";
import { fieldLabels } from "../../sections/fieldLabels";
import { Activity } from "@ofqual/portal-activity-feed";
import {
  getLookupTracker
} from "@ofqual/portal-core/core/utils/modelHelpers";

const useStyles = makeStyles((theme) => ({
  changeDetails: {
    padding: 15,
  },
  actions: {
    padding: 15,
  },
}));

export type RecordEventCompletionDialogProps = {
  foiItem: FoiItem | null;
  activity: Activity;
  isAdmin: boolean;
  onClose: () => void;
  action: "approve" | "reject" | "complete"
};

const foiActivityToModel = (activity: Activity) => {
  const body: Pick<
    FoiEventComplete,
    | "foiId"
    | "meetingDate"
    | "foiEventType"
    | "id"
    | "referenceId"
  > = JSON.parse(activity.activityMetaData);

  return {
    foiId: body.foiId,
    foiEventType: body.foiEventType,
    meetingDate: body.meetingDate,
    files: [],
    notes: "",
    wasAttended: false,
    referenceId: body.referenceId
  } as Partial<FoiEventComplete>
};

export const RecordEventCompletionDialog: React.FC<RecordEventCompletionDialogProps> = ({
  foiItem,
  activity,
  onClose,
  action,
}) => {
  const classes = useStyles();
  const originalEvent = foiActivityToModel(activity);
  console.log(originalEvent);

  const {
    handleSubmit,
    control,
    errors,
  } = useForm<FoiEventComplete>({
    defaultValues: { ...originalEvent, files: [] },
  });

  const dispatch = useDispatch();

  const lookupTracker = useMemo(() => getLookupTracker(), []);

  const onSubmit = (completedEvent: FoiEventComplete) => {
    completedEvent.foiId = originalEvent.foiId;
    
    dispatch(
      completeFoiEvent(
        foiItem?.id!, 
        originalEvent?.referenceId!,
        completedEvent
      )
    );
    onClose();
  };

  const [foiEventTypeLookups] = useLookup(foiEventTypeService);
  const requiredFile = originalEvent.foiEventType !== 31;

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-test="add-event-dialog"
    >
      <DialogTitle data-test="add-event-title">Record completion - {action}</DialogTitle>
      <Divider component="div" />
      <DialogContent>
        <form>
          <div className={classes.changeDetails}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="meetingDate"
                  as={
                    <DateTimePicker
                      dataTest="save-add-event-date"
                      label={fieldLabels.meetingDate}
                      readOnly
                    />
                  }
                />
              </Grid>
              <Grid item md={12}>
                <Controller
                  control={control}
                  name="foiEventType"
                  as={
                    <Select
                      selectVariant="single"
                      label={fieldLabels.foiEventType}
                      datatest="save-event-type"
                      readOnly
                      lookupTracker={lookupTracker}
                    >
                      {foiEventTypeLookups}
                    </Select>
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="notes"
                  rules={{ required: true, maxLength: 5000 }}
                  as={
                    <TextField
                      variant="outlined"
                      label={action === "complete"
                        ? "Notes"
                        : "Rationale for decision"}
                      data-test="save-event-notes"
                      fullWidth
                      multiline
                      rows={4}
                      maxRows={8}
                      required
                    />
                  }
                />
                {errors.notes && errors.notes.type === "required" && (
                  <Error data-test="rationale-for-decision-validation">This field is required</Error>
                )}
                {errors.notes && errors.notes.type === "maxLength" && (
                  <Error>Note must be less than 5000 characters</Error>
                )}
              </Grid>
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="files"
                  rules={{ validate: (value) => !requiredFile || !!value?.length }}
                  as={<FileInput multiple={true} />}
                />
                {errors.files && (
                  <Error data-test="complete-event-files-validation">
                    &nbsp;&nbsp;Please select one or more files to attach
                  </Error>
                )}
              </Grid>
              <Controller
                name="action"
                control={control}
                defaultValue={action}
                as={
                  <input type="hidden" value={action} />
                } />
            </Grid>
          </div>
        </form>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button
          onClick={onClose}
          data-test="cancel-record-completion-button"
          color="primary"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          data-test="add-record-completion-button"
          color="primary"
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
